import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [];

  connect() {
    // Don't show modal on initial page load
    // Listen for Escape key
    this.handleEscape = this.handleEscape.bind(this);
    document.addEventListener("keydown", this.handleEscape);

    // Listen for backdrop clicks
    this.element.addEventListener("click", this.handleBackdropClick.bind(this));

    // Watch for turbo-frame content changes
    const modalContent = this.element.querySelector("#modal_content");
    if (modalContent) {
      // Check if there's already content on page load
      if (
        modalContent.innerHTML.trim() &&
        !modalContent.innerHTML.includes(
          "<!-- Auth forms will be loaded here -->"
        )
      ) {
        this.show();
      }

      // Listen for turbo:frame-load event on the document (bubbles up)
      document.addEventListener("turbo:frame-load", (event) => {
        if (event.detail?.target?.id === "modal_content") {
          if (modalContent.innerHTML.trim()) {
            this.show();
          }
        }
      });

      // Also use mutation observer as fallback
      this.observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList" && mutation.addedNodes.length > 0) {
            // Check if meaningful content was added (not just whitespace or comments)
            const hasRealContent = Array.from(mutation.addedNodes).some(
              (node) => {
                if (node.nodeType === Node.ELEMENT_NODE) return true;
                if (node.nodeType === Node.TEXT_NODE)
                  return node.textContent.trim().length > 0;
                return false;
              }
            );

            if (hasRealContent) {
              this.show();
            }
          } else if (
            mutation.type === "childList" &&
            mutation.removedNodes.length > 0 &&
            modalContent.innerHTML.trim() === ""
          ) {
            this.hide();
          }
        });
      });

      this.observer.observe(modalContent, {
        childList: true,
        subtree: true,
      });
    }
  }

  disconnect() {
    // Clean up event listeners
    document.removeEventListener("keydown", this.handleEscape);

    // Clean up observer
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  show() {
    this.element.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
  }

  hide() {
    this.element.classList.add("hidden");
    document.body.classList.remove("overflow-hidden");
  }

  handleEscape(event) {
    if (event.key === "Escape") {
      this.hide();
    }
  }

  handleBackdropClick(event) {
    // Only close if clicking the backdrop (not the modal content)
    if (event.target === this.element) {
      this.hide();
    }
  }

  showImage(event) {
    const imageUrl = event.currentTarget.dataset.modalImageUrl;
    const imageAlt = event.currentTarget.dataset.modalImageAlt || "Image";

    if (!imageUrl) return;

    // Get the modal content area
    const modalContent = this.element.querySelector("#modal_content");
    if (!modalContent) return;

    // Create image modal HTML
    const imageModalHTML = `
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
          <img src="${imageUrl}"
               alt="${imageAlt}"
               class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
               style="max-height: 90vh;">
          <button class="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  data-action="click->modal#hide">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `;

    // Set the content and show the modal
    modalContent.innerHTML = imageModalHTML;
    this.show();
  }

  // Global static method to show image from anywhere
  static showImageModal(imageUrl, imageAlt) {
    const modal = document.getElementById("auth_modal");
    const modalContent = modal?.querySelector("#modal_content");

    if (!modal || !modalContent) return;

    const imageModalHTML = `
      <div class="flex items-center justify-center min-h-screen p-4">
        <div class="relative max-w-4xl max-h-[90vh] w-full h-full flex items-center justify-center">
          <img src="${imageUrl}"
               alt="${imageAlt}"
               class="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
               style="max-height: 90vh;">
          <button class="absolute top-4 right-4 bg-black/50 hover:bg-black/70 text-white rounded-full p-2 transition-colors"
                  onclick="document.getElementById('auth_modal').classList.add('hidden'); document.body.classList.remove('overflow-hidden');">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>
      </div>
    `;

    modalContent.innerHTML = imageModalHTML;
    modal.classList.remove("hidden");
    document.body.classList.add("overflow-hidden");
  }
}
